import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { CheckCircle, XCircle, Loader2, Crown } from 'lucide-react';

export default function SubscriptionSuccess() {
  const user = useAuthStore((s) => s.user);
  const setAuth = useAuthStore((s) => s.setAuth);
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');
  const isDemo = searchParams.get('demo') === 'true';

  useEffect(() => {
    if (isDemo) {
      // Demo mode - subscription already activated by backend
      setStatus('success');
      setAuth({ ...user, subscription_tier: 'premium' });
    } else if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId, isDemo]);

  const pollPaymentStatus = async () => {
    if (attempts >= 5) {
      setStatus('error');
      return;
    }

    try {
      const response = await api.get(`/subscriptions/status/${sessionId}`);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        // Update user context with new subscription
        setAuth({ ...user, subscription_tier: 'premium' });
      } else if (response.data.status === 'expired') {
        setStatus('error');
      } else {
        // Keep polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="subscription-success">
      <Card className="max-w-md w-full">
        <CardContent className="p-4 sm:p-8 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto mb-4 text-teal-500 animate-spin" />
              <h2 className="text-2xl font-bold text-slate-900 mb-2 font-['Manrope']">
                Processing Payment
              </h2>
              <p className="text-slate-600">
                Please wait while we confirm your payment...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-teal-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2 font-['Manrope']">
                Welcome to Premium!
              </h2>
              <p className="text-slate-600 mb-6">
                Your subscription is now active. Enjoy unlimited access to all ElderLink features.
              </p>
              <div className="bg-teal-50 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-center gap-2 text-teal-700">
                  <Crown className="w-5 h-5" />
                  <span className="font-medium">Premium Member</span>
                </div>
              </div>
              <Link to="/dashboard">
                <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="go-dashboard-btn">
                  Go to Dashboard
                </Button>
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-rose-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2 font-['Manrope']">
                Payment Issue
              </h2>
              <p className="text-slate-600 mb-6">
                We couldn't confirm your payment. If you were charged, please contact support.
              </p>
              <div className="flex gap-3">
                <Link to="/subscription" className="flex-1">
                  <Button variant="outline" className="w-full">
                    Try Again
                  </Button>
                </Link>
                <Link to="/dashboard" className="flex-1">
                  <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white">
                    Dashboard
                  </Button>
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
